<template>
  <PublicShell :color="page && page.brand ? page.brand.color : null" narrow>
    <div v-if="loading" class="pf-loading" role="status"><span class="pb-sr-only">Loading…</span>
      <span class="pb-skeleton pf-sk1"></span><span class="pb-skeleton pf-sk2"></span><span class="pb-skeleton pf-sk3"></span></div>
    <div v-else-if="missing || unavailable" class="pf-message">
      <div v-if="page && page.brand" class="pf-brand"><img v-if="logoUrl" :src="logoUrl" alt="" class="pf-logo"><strong>{{ page.brand.name }}</strong></div>
      <span class="pb-empty-icon" aria-hidden="true"><i :class="['mdi', missing ? 'mdi-link-variant-off' : 'mdi-pause-circle-outline']"></i></span>
      <h1 class="pf-title display-font">{{ missing ? 'Page not found' : 'Not accepting responses' }}</h1>
      <p class="pb-body">{{ missing ? missingText : unavailableText }}</p>
    </div>
    <div v-else-if="loadError" class="pf-message" role="alert">
      <h1 class="pf-title display-font">We couldn't open this page</h1>
      <p class="pb-body">Check your connection and try again.</p>
      <button type="button" class="pb-button" @click="load">Try again</button>
    </div>
    <div v-else class="pf-card">
      <CollectFormPreview ref="form" mode="live" :form="page.form" :brand="formBrand" :attribution="page.attribution.show"
                          :landing-url="page.attribution.href" :privacy-url="privacyUrl" :sending="sending" :error="error" :result="result"
                          @submit="submit" />
    </div>
  </PublicShell>
</template>

<script>
import CollectFormPreview from '../components/CollectFormPreview.vue';
import PublicShell from '../components/PublicShell.vue';
import { get, uploadForm, message } from '../api';
import { imageUrl } from '../publicUrls';

const NETWORK = 'We couldn\'t send your answers. Your text is still here; please try again.';
const TOO_MANY = 'Too many submissions from this connection. Please try again in a few minutes.';

export default {
  name: 'PublicFormView',
  components: { CollectFormPreview, PublicShell },
  data: () => ({ page: null, loading: true, missing: false, unavailable: false, loadError: false, sending: false, error: '', result: null,
    missingText: 'This feedback page doesn\'t exist. Check the link you were sent.',
    unavailableText: 'This feedback page isn\'t accepting responses right now.' }),
  computed: {
    key() { return String(this.$route.params.key || ''); },
    logoUrl() { return this.page && this.page.brand && this.page.brand.logo ? imageUrl(this.page.brand.logo) : ''; },
    formBrand() { const brand = this.page.brand; return { name: brand.name, color: brand.color, logo_url: this.logoUrl, privacy_url: brand.privacy_url }; },
    privacyUrl() { return ((this.$config && this.$config.LANDING_PAGE_URL) || '') + '/privacy'; }
  },
  mounted() { this.load(); },
  methods: {
    async load(keepState) {
      if (!keepState) this.loading = true;
      this.loadError = false;
      try {
        const page = await get('/public/forms/' + this.key);
        if (!page.available) { this.unavailable = true; this.page = page; return; }
        this.page = page;
        document.title = page.brand.name + ' · ' + (page.form.headline || 'Share your feedback');
      } catch (error) {
        if (error.response && error.response.status === 404) this.missing = true; else this.loadError = true;
      } finally { this.loading = false; }
    },
    async submit(payload, photo) {
      this.sending = true;
      this.error = '';
      const body = new FormData();
      body.append('payload', JSON.stringify(Object.assign({ token: this.page.token }, payload)));
      if (photo) body.append('photo', photo);
      try {
        this.result = await uploadForm('/public/forms/' + this.key + '/submissions', body);
        window.scrollTo(0, 0);
      } catch (error) {
        const response = error.response;
        const text = message(error);
        if (!response) this.error = NETWORK;
        else if (response.status === 429) this.error = TOO_MANY;
        else if (response.status === 410) this.unavailable = true;
        else if (/photo/i.test(text)) {
          // The full sentence sits under the photo field; the summary only points to it.
          this.$refs.form.photoFailed(text);
          this.error = 'The photo couldn\'t be read; it\'s marked below. Your answers are still here.';
        }
        else {
          this.error = text;
          // An old or too-quick page token: fetch a fresh one; the answers stay in the form.
          if (/long time|moment/i.test(text)) this.load(true);
        }
      } finally { this.sending = false; }
    }
  }
};
</script>

<style scoped>
.pf-card{background:#fff;border:1px solid #E6DED2;border-radius:20px;padding:36px 32px;box-shadow:0 1px 2px rgba(31,35,40,.04),0 12px 32px rgba(31,35,40,.06)}
.pf-message{text-align:center;padding:48px 24px;background:#fff;border:1px solid #E6DED2;border-radius:20px}
.pf-message .pb-body{max-width:420px;margin:10px auto 20px}
.pf-brand{display:flex;align-items:center;justify-content:center;gap:10px;margin-bottom:24px;font-size:16px}
.pf-logo{width:40px;height:40px;border-radius:10px;object-fit:contain;border:1px solid #E6DED2;background:#fff}
.pf-title{font:500 28px/1.2 var(--font-display)!important;margin:0}
.pf-loading{display:grid;gap:14px;padding:40px 32px;background:#fff;border:1px solid #E6DED2;border-radius:20px}
.pf-sk1{height:20px;width:40%}
.pf-sk2{height:34px;width:85%}
.pf-sk3{height:140px;width:100%}
@media(max-width:600px){.pf-card{padding:22px 16px;border-radius:16px}}
</style>
